// @wc-ignore-file

import { retainSearchParams } from "@tanstack/react-router";

/**
 * CR-144: the Settings page (/account) keeps the workspace + project the user
 * came from as search params, so the header can keep showing that context.
 * The values are untrusted URL input: anything that is not a non-empty string
 * is dropped, and the header only uses them once the workspace resolves among
 * the user's own workspaces.
 */
export const SETTINGS_CONTEXT_SEARCH_KEYS = ["workspaceSlug", "projectSlug"] as const;

export type SettingsContextSearch = {
  workspaceSlug?: string;
  projectSlug?: string;
};

const MAX_SLUG_LENGTH = 200;

function asSlug(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_SLUG_LENGTH) {
    return undefined;
  }
  return trimmed;
}

export function validateSettingsContextSearch(
  search: Record<string, unknown>,
): SettingsContextSearch {
  // Invalid values are OMITTED, never returned as `undefined`: the route's
  // retainSearchParams middleware only carries a key the next location does
  // not already have, and a present-but-undefined key would defeat it. The
  // raw search is still merged under this result by the router, so readers
  // re-validate (see useHeaderContext) instead of trusting the match search.
  const result: SettingsContextSearch = {};
  const workspaceSlug = asSlug(search.workspaceSlug);
  if (!workspaceSlug) {
    return result;
  }
  result.workspaceSlug = workspaceSlug;
  // A project only means something inside its workspace.
  const projectSlug = asSlug(search.projectSlug);
  if (projectSlug) {
    result.projectSlug = projectSlug;
  }
  return result;
}

/**
 * Route options for /account: validate the context and keep it on every link
 * and navigation inside Settings (General, API Keys, integrations) without
 * touching each Link.
 */
export const settingsContextRouteOptions = {
  validateSearch: validateSettingsContextSearch,
  search: {
    middlewares: [retainSearchParams<SettingsContextSearch>([...SETTINGS_CONTEXT_SEARCH_KEYS])],
  },
};
